import React from "react";
import "./css/blogdetails.css";
import banner from "../../../../assets/imgs/grant-lifecycles.jpg";
import NewsLetter from "../../../Shared/NewsLetter";
import newsletterbanner from "../../../../assets/imgs/newsletter-img.webp";
import rcCtaLogo from "../../../../assets/imgs/rc-cta-logo.png";
import { Helmet } from "react-helmet";

const TheGrantLifecycle = () => {
  return (
    <>
      <Helmet>
        <title>The Grant Lifecycle</title>
        <meta name="keywords" content="" />
        <meta
          name="description"
          content="Master donor management for nonprofits using a cloud-based CRM. Enhance donor engagement, improve retention, and streamline your fundraising efforts."
        />
      </Helmet>
      <div>
        <section>
          <div class="contact-banner">
            <div class="innerPage-banner-img">
              <img
                src={banner}
                alt="Your Absolute Guide to Nonprofit Fundraising Success"
                width="1600"
                height="450"
              />
            </div>
          </div>
        </section>
        <section class="blog_Section">
          <div class="container">
            <div class="row ppc-ds">
              <div class="col-md-8 col-lg-8">
                <div class="fullBlog-content">
                  <h1 class="h1">
                    The Grant Lifecycle, Demystified: A 10-Phase Playbook for
                    Nonprofit Leaders Evaluating Modern Grants CRM
                  </h1>
                  <p>
                    A field guide from the Right Cause team, a Microsoft
                    Solutions Partner working with mission-driven organizations
                  </p>
                  <p>
                    If you lead a nonprofit, you already know grants are not
                    just revenue. They are commitments. Every award you accept
                    comes with a period of performance, a reporting calendar,
                    and a set of obligations that your board, your auditor, and
                    your funder all expect you to honor.
                  </p>

                  <p>
                    We work with nonprofit leaders every week who are trying to
                    grow their grants portfolio without growing their compliance
                    risk at the same pace. The conversation almost always starts
                    the same way. We've outgrown spreadsheets. Our development
                    team is using one tool, finance is in another, and program
                    leads are flying blind. Something has to change before our
                    next audit, or before we lose another opportunity to a
                    missed deadline.
                  </p>

                  <p>
                    This guide is for that conversation. It walks through the
                    ten phases of the grant lifecycle the way we walk through
                    them with executive directors, COOs, and finance leaders
                    during discovery. At each phase, we name the real-world
                    pressure points and show how the &nbsp;
                    <a
                      href="https://www.rightcause.net/solutions/grants-management-solution"
                      target="_blank"
                    >
                      Right Cause Grants Tracking
                    </a>{" "}
                    &nbsp; Software, built on the Microsoft Cloud, helps your
                    team move faster with fewer mistakes.
                  </p>

                  <p>
                    We are a Microsoft Solutions Partner. That matters here
                    because most of the nonprofits we serve are already running
                    Microsoft 365, already have Teams in daily use, and many are
                    exploring or actively using Dynamics 365 and Power Platform
                    through Microsoft's nonprofit licensing programs. Right
                    Cause is designed to extend that investment, not replace it.
                  </p>

                  <h2 className="mar-top10" id="general-purpose-crm">
                    Why a General-Purpose CRM Is Not Enough
                  </h2>
                  <p>
                    The honest version: a generic CRM can hold contact records
                    and a pipeline. It cannot run a grant.
                  </p>
                  <p>
                    Grants come with structures that consumer-grade or
                    sales-oriented CRMs were never designed for:
                  </p>

                  <ul>
                    <li>
                      Period of performance tracking with defined start and end
                      dates{" "}
                    </li>
                    <li>
                      Indirect cost rates and federally negotiated rate
                      agreements{" "}
                    </li>
                    <li>
                      Drawdown schedules tied to expenditure documentation{" "}
                    </li>
                    <li>
                      Subrecipient risk assessments and pass-through
                      monitoring{" "}
                    </li>
                    <li>
                      Uniform Guidance (2 CFR 200) compliance for any
                      organization receiving federal funds{" "}
                    </li>
                    <li>
                      Audit-ready trails where every action, approval, and
                      version is documented{" "}
                    </li>
                  </ul>
                  <p>
                    Your team needs a system that handles all of this without
                    forcing your finance director to keep a parallel spreadsheet
                    "just in case." That is the gap Right Cause Grants grant
                    management software was built to close.
                  </p>

                  <h2 className="mar-top10" id="grants-management-process">
                    The 10 Phases of Grants Management Process at a Glance
                  </h2>
                  {/* table start  */}
                  <div class="grant-lifecycle-table-wrapper">
                    <table class="grant-lifecycle-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Phase</th>
                          <th>What Your Team Is Actually Doing</th>
                          <th>Where It Tends to Break</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>1</td>
                          <td>
                            <span>Prospecting</span>
                          </td>
                          <td>Researching funders worth pursuing</td>
                          <td>Opportunities slip past deadlines</td>
                        </tr>
                        <tr>
                          <td>2</td>
                          <td>
                            <span>Cultivation</span>
                          </td>
                          <td>Building relationships before applying</td>
                          <td>Institutional memory walks out the door</td>
                        </tr>
                        <tr>
                          <td>3</td>
                          <td>
                            <span>Proposal Development</span>
                          </td>
                          <td>Writing, budgeting, submitting</td>
                          <td>Version chaos, last-minute approvals</td>
                        </tr>
                        <tr>
                          <td>4</td>
                          <td>
                            <span>Award Decision</span>
                          </td>
                          <td>Negotiating, signing the agreement</td>
                          <td>Handoff gaps between teams</td>
                        </tr>
                        <tr>
                          <td>5</td>
                          <td>
                            <span>Award Setup</span>
                          </td>
                          <td>Activating the grant operationally</td>
                          <td>Systems do not sync</td>
                        </tr>
                        <tr>
                          <td>6</td>
                          <td>
                            <span>Fund Disbursement</span>
                          </td>
                          <td>Moving money in or out</td>
                          <td>Drawdown errors, payment delays</td>
                        </tr>
                        <tr>
                          <td>7</td>
                          <td>
                            <span>Implementation</span>
                          </td>
                          <td>Tracking spends against budget</td>
                          <td>Variance caught too late</td>
                        </tr>
                        <tr>
                          <td>8</td>
                          <td>
                            <span>Reporting</span>
                          </td>
                          <td>Proving outcomes to funders</td>
                          <td>Reports rebuilt from scratch every cycle</td>
                        </tr>
                        <tr>
                          <td>9</td>
                          <td>
                            <span>Close-Out</span>
                          </td>
                          <td>Wrapping up cleanly</td>
                          <td>Audit findings emerge here</td>
                        </tr>
                        <tr>
                          <td>10</td>
                          <td>
                            <span>Stewardship</span>
                          </td>
                          <td>Keeping the relationship warm</td>
                          <td>Renewals start from a blank page</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {/* table end  */}

                  <h2 className="mar-top10" id="phase-1">
                    Phase 1: Prospecting and Opportunity Management
                  </h2>

                  <p>
                    {" "}
                    <strong>What's happening on your team: </strong>
                    Your development director is scanning Grants.gov, Candid,
                    foundation websites, and 990-PFs to find funders aligned
                    with your mission. Maybe a board member forwarded a tip
                    about a corporate funder. That note is now somewhere in an
                    inbox.
                  </p>

                  <p>
                    <strong>Where it goes wrong: </strong>
                  </p>
                  <ul>
                    <li>
                      Prospect research falls off the calendar when active
                      grants demand attention{" "}
                    </li>
                    <li>
                      Two staff members independently chase the same
                      opportunity{" "}
                    </li>
                    <li>
                      The pipeline spreadsheet has not been updated since the
                      last development hire left{" "}
                    </li>
                  </ul>

                  <p>
                    <strong>
                      Funder prospect research and opportunity tracking with
                      Right Cause:{" "}
                    </strong>
                  </p>

                  <ul>
                    <li>
                      Funder profiles with giving history, program priorities,
                      and decision-makers{" "}
                    </li>
                    <li>
                      Opportunity pipeline with fit scores, deadline alerts, and
                      clear ownership{" "}
                    </li>
                    <li>
                      Single dashboard your ED can open before every leadership
                      meeting{" "}
                    </li>
                    <li>
                      Native integration with Outlook so emails to and from
                      program officers are captured automatically{" "}
                    </li>
                  </ul>

                  <h2 className="mar-top10" id="phase-2">
                    Phase 2: Cultivation and Pre-Application
                  </h2>

                  <p>
                    {" "}
                    <strong>What's happening on your team: </strong>
                    Your director is building relationships with program
                    officers, drafting Letters of Inquiry, and developing
                    concept notes. The slow, patient work that determines
                    whether a proposal even gets invited.
                  </p>
                  <p>
                    <strong>Where it goes wrong: </strong>
                  </p>
                  <ul>
                    <li>
                      A program officer retires and the relationship history was
                      never documented{" "}
                    </li>
                    <li>LOI status gets buried in email threads </li>
                    <li>
                      Your team commits two weeks to a full proposal without a
                      structured go or no-go decision{" "}
                    </li>
                  </ul>

                  <p>
                    <strong>
                      Funder cultivation and LOI management with Right
                      Cause:{" "}
                    </strong>
                  </p>

                  <ul>
                    <li>
                      Touchpoint logging tied to funder organizations and
                      individual contacts{" "}
                    </li>
                    <li>
                      LOI status tracking with funder response history visible
                      to the whole team{" "}
                    </li>
                    <li>
                      Configurable go/no-go workflows so leadership signs off
                      before the work begins{" "}
                    </li>
                    <li>
                      Concept note version history stored in SharePoint,
                      accessible from Teams{" "}
                    </li>
                  </ul>

                  <h2 className="mar-top10" id="phase-3">
                    Phase 3: Proposal Development and Submission
                  </h2>

                  <p>
                    {" "}
                    <strong>What's happening on your team: </strong>A full
                    application is in motion. Narrative, methodology, logic
                    model, budget with indirect costs, letters of support, and a
                    stack of attachments. Multiple people are editing different
                    sections.
                  </p>

                  <p>
                    <strong>Where it goes wrong:</strong>
                  </p>

                  <ul>
                    <li>Version control breaks down across drafts </li>
                    <li>
                      The budget worksheet and the narrative do not match by
                      submission day{" "}
                    </li>
                    <li>
                      Approval routing depends on whoever happens to be checking
                      email at 9 PM{" "}
                    </li>
                    <li>
                      Submission portal failures the night before deadline{" "}
                    </li>
                  </ul>

                  <p>
                    <strong>
                      Proposal workflow and submission tracking with Right
                      Cause:{" "}
                    </strong>
                  </p>

                  <ul>
                    <li>
                      Centralized proposal artifacts with full version history
                      in SharePoint{" "}
                    </li>
                    <li>
                      Budget templates with indirect cost rate logic and
                      cost-share calculations{" "}
                    </li>
                    <li>
                      Configurable internal review and approval routing through
                      Power Automate{" "}
                    </li>
                    <li>
                      Submission log with portal IDs, confirmation receipts, and
                      submitted PDFs archived on the record{" "}
                    </li>
                  </ul>

                  <h2 className="mar-top10" id="phase-4">
                    Phase 4: Award Decision and Agreement
                  </h2>

                  <p>
                    <strong>What's happening on your team: </strong>
                    The funder reviews. Sometimes there is a clarification
                    request. Eventually you receive an award letter, a
                    declination, or an invitation to revise. Negotiation
                    happens. The agreement has been signed.
                  </p>

                  <p>
                    <strong>Where it goes wrong: </strong>
                  </p>

                  <ul>
                    <li>
                      The signed agreement gets emailed to one person and
                      forwarded twice before anyone files it correctly{" "}
                    </li>
                    <li>
                      Special conditions buried in the PDF are missed
                      entirely{" "}
                    </li>
                    <li>
                      Period of performance start dates are not communicated to
                      the program team running point{" "}
                    </li>
                  </ul>

                  <p>
                    <strong>
                      Grant award management and agreement tracking with Right
                      Cause:{" "}
                    </strong>
                  </p>

                  <ul>
                    <li>
                      Decision status tracking with reason codes that feed back
                      into prospecting intelligence{" "}
                    </li>
                    <li>
                      Award letter and grant agreement become the system of
                      record, not an email attachment{" "}
                    </li>
                    <li>
                      Special conditions captured as structured, searchable data
                      fields
                    </li>
                    <li>
                      Automated handoff notifications to program and finance the
                      moment an award is activated{" "}
                    </li>
                  </ul>

                  <h2 className="mar-top10" id="phase-5">
                    Phase 5: Award Setup and Activation
                  </h2>

                  <p>
                    {" "}
                    <strong>What's happening on your team: </strong>
                    Finance is creating fund codes and project IDs. Subaward
                    agreements are going to partner organizations. Compliance is
                    registering the reporting calendar. The program team is
                    being pulled into a kickoff before the grant quietly goes
                    live without them.
                  </p>

                  <p>
                    <strong>Where it goes wrong: </strong>
                  </p>

                  <ul>
                    <li>
                      CRM, accounting software, and project tools each end up
                      with their own version of the truth by week two{" "}
                    </li>
                    <li>
                      Subrecipient onboarding lags, and you cannot legally
                      disburse until risk assessments are complete{" "}
                    </li>
                    <li>
                      Reporting deadlines exist on paper but never land on
                      anyone's actual calendar{" "}
                    </li>
                    <li>
                      Special conditions get skimmed instead of
                      operationalized{" "}
                    </li>
                  </ul>

                  <p>
                    <strong>
                      Award activation and grant compliance setup automation
                      with Right Cause:{" "}
                    </strong>
                  </p>

                  <ul>
                    <li>
                      Award data syncs across CRM, finance, and program
                      functions on activation{" "}
                    </li>
                    <li>
                      Reporting calendar auto-generates from funder-specific
                      terms and pushes to Outlook calendars{" "}
                    </li>
                    <li>
                      Compliance checklists differ by funder type (federal,
                      foundation, corporate) and load automatically{" "}
                    </li>
                    <li>
                      Kickoff workflow templates pull program, finance, and
                      compliance into the same Teams channel{" "}
                    </li>
                  </ul>

                  <div className="real-world-case-study-box">
                    <p>
                      <strong>Real-world Case Study</strong>
                    </p>
                    <p>
                      <strong>Client:</strong> A leading non-profit committed to
                      improving healthcare access, the client is highly regarded
                      for its pioneering chronic disease management programs
                      serving thousands of patients nationwide.
                    </p>

                    <p>
                      <strong>Challenge Faced: </strong>
                    </p>

                    <ul>
                      <li>Disparate manual processes,</li>
                      <li>No automated auditing of clinical records, </li>
                      <li>No centralized system, </li>
                      <li>Patients had no standardized resources </li>
                    </ul>

                    <p>
                      <strong> Solution Provided:</strong> We helped our client
                      by developing Health Professional Hub Portal. We helped
                      them design interactive dashboards while providing a
                      workflow system powered referral management with routing
                      and approval process.
                      <br />
                      <a href="#">Download the case study.</a>
                    </p>
                  </div>

                  <h2 className="mar-top10" id="phase-6">
                    Phase 6: Fund Allocation and Disbursement
                  </h2>

                  <p>
                    {" "}
                    <strong>What's happening on your team: </strong>
                    Money is moving, and how it moves depends entirely on the
                    funder. A family foundation might wire the full grant
                    upfront. Federal agencies make you spend first and reimburse
                    through drawdowns. Multi-year cooperative agreements release
                    funds in milestone tranches tied to deliverables.
                  </p>

                  <p>
                    <strong>Where does it goes wrong:</strong>
                  </p>
                  <ul>
                    <li>
                      Cash flow forecasting breaks when finance is reconciling
                      drawdowns manually{" "}
                    </li>
                    <li>
                      Subrecipient payments get delayed because someone is
                      waiting on an invoice format{" "}
                    </li>
                    <li>
                      Federal drawdown timing errors trigger funder holds, and
                      those are painful to unwind{" "}
                    </li>
                    <li>
                      Allocations across multi-program grants lose traceability
                      fast{" "}
                    </li>
                  </ul>

                  <p>
                    <strong>
                      Grant disbursement and fund allocation workflows with
                      Right Cause:{" "}
                    </strong>
                  </p>

                  <ul>
                    <li>
                      Disbursement schedules track every payment event with
                      status visibility for finance and leadership{" "}
                    </li>
                    <li>
                      Drawdown requests generate with supporting documentation
                      attached automatically{" "}
                    </li>
                    <li>
                      Program fund allocation traces to the line item, not just
                      the grant{" "}
                    </li>
                    <li>
                      Subrecipient disbursements run through the same workflow
                      as direct expenses{" "}
                    </li>
                    <li>
                      Integrates with Microsoft Dynamics 365 Business Central
                      and other accounting systems your finance team already
                      uses{" "}
                    </li>
                  </ul>

                  <h2 className="mar-top10" id="phase-7">
                    Phase 7: Implementation and Budget Monitoring
                  </h2>

                  <p>
                    {" "}
                    <strong> What's happening on your team: </strong>
                    Expenses are posting. Burn rate is being tracked against the
                    period of performance. Budget vs. actual reports are going
                    to leadership. Modifications, including no-cost extensions,
                    budget realignments, and scope changes, are being negotiated
                    with the funder.
                  </p>

                  <p>
                    <strong>Where it goes wrong: </strong>
                  </p>

                  <ul>
                    <li>
                      Variance issues surface in the monthly finance report, two
                      weeks after you could have fixed them{" "}
                    </li>
                    <li>
                      Subaward monitoring gets the lightest touch and produces
                      the largest audit findings{" "}
                    </li>
                    <li>
                      Modification approvals happen over email and are not
                      documented anywhere defensible{" "}
                    </li>
                    <li>
                      Unallowable costs slip through because nobody is checking
                      against the specific funder's terms
                    </li>
                  </ul>

                  <p>
                    <strong>
                      Real-time budget monitoring and subrecipient oversight
                      with Right Cause:{" "}
                    </strong>
                  </p>

                  <ul>
                    <li>
                      Real-time burn rate and variance dashboards in Power BI,
                      not month-end PDFs{" "}
                    </li>
                    <li>
                      Unallowable costs get flagged at entry, by funder
                      rule{" "}
                    </li>
                    <li>
                      Modification requests run as a formal workflow with full
                      audit trail{" "}
                    </li>
                    <li>
                      Risk-based subrecipient monitoring with structured site
                      visit documentation{" "}
                    </li>
                  </ul>

                  <h2 className="mar-top10" id="phase-8">
                    Phase 8: Monitoring, Evaluation, and Reporting
                  </h2>

                  <p>
                    {" "}
                    <strong>What's happening on your team: </strong>
                    Funders want proof their money worked. Performance reports
                    against KPIs, financial reports like the SF-425, beneficiary
                    data, M&E frameworks, and audit preparation all live in this
                    phase.
                  </p>

                  <p>
                    <strong>Where it goes wrong: </strong>
                  </p>

                  <ul>
                    <li>
                      Every reporting cycle is rebuilt from scratch instead of
                      pulled from existing data{" "}
                    </li>
                    <li>
                      Program data lives in one system, financial data in
                      another, reconciled under deadline pressure{" "}
                    </li>
                    <li>
                      Beneficiary tracking is not rigorous enough to defend the
                      outcome claims in the report{" "}
                    </li>
                    <li>
                      Audit prep starts the week before the auditor arrives{" "}
                    </li>
                  </ul>

                  <p>
                    <strong>
                      Grant reporting automation and M&E tracking with Right
                      Cause:{" "}
                    </strong>
                  </p>

                  <ul>
                    <li>
                      Reports generate from structured data, not assembled from
                      scratch{" "}
                    </li>
                    <li>
                      KPIs track against the logic model you submitted with the
                      proposal{" "}
                    </li>
                    <li>
                      M&E frameworks aligned to OECD-DAC standards for
                      international grants{" "}
                    </li>
                    <li>
                      Full audit trail of every report, every version, every
                      submission, retained per funder rules{" "}
                    </li>
                  </ul>

                  <h2 className="mar-top10" id="phase-9">
                    Phase 9: Close-Out
                  </h2>

                  <p>
                    {" "}
                    <strong>What's happening on your team: </strong>
                    Final narrative, financial, and performance reports are due,
                    typically 90 to 120 days after the period of performance
                    ends. Unspent funds need to be returned. Equipment purchased
                    with grant dollars needs disposition. Records get archived
                    for three to seven years, sometimes longer.
                  </p>

                  <p>
                    <strong>Where it goes wrong: </strong>
                  </p>

                  <ul>
                    <li>
                      Close-out gets treated as an afterthought, which is
                      exactly when audit findings appear{" "}
                    </li>
                    <li>
                      Unobligated balances are not reconciled and the funder
                      asks pointed questions{" "}
                    </li>
                    <li>
                      Records scatter across departing staff laptops and shared
                      drives nobody owns{" "}
                    </li>
                    <li>
                      Asset disposition documentation is missing because no one
                      was tracking the equipment{" "}
                    </li>
                  </ul>

                  <p>
                    <strong>
                      Grant close-out workflows and audit-ready archival with
                      Right Cause:{" "}
                    </strong>
                  </p>

                  <ul>
                    <li>
                      Close-out checklists trigger automatically as the period
                      of performance approaches{" "}
                    </li>
                    <li>
                      Final disbursement reconciliation runs as a guided
                      workflow{" "}
                    </li>
                    <li>
                      Asset disposition documentation lives with the grant
                      record{" "}
                    </li>
                    <li>
                      Full grant archive (proposal through final report)
                      retained in SharePoint per funder rules{" "}
                    </li>
                  </ul>

                  <h2 className="mar-top10" id="phase-10">
                    Phase 10: Stewardship and Renewal
                  </h2>

                  <p>
                    {" "}
                    <strong>What's happening on your team: </strong>
                    The grant closes, but the relationship should not.
                    Thank-you, impact stories, renewal applications, and lessons
                    learned that feed the next prospecting cycle all happen
                    here.
                  </p>
                  <p>
                    <strong>Where it goes wrong: </strong>
                  </p>
                  <ul>
                    <li>
                      Stewardship gets deprioritized the moment the grant
                      closes{" "}
                    </li>
                    <li>
                      Funders hear from you only when you need money again{" "}
                    </li>
                    <li>
                      Renewal applications start from a blank page instead of a
                      documented relationship{" "}
                    </li>
                    <li>
                      Lessons learned die in a Teams channel and never reach the
                      prospecting team
                    </li>
                  </ul>
                  <p>
                    <strong>
                      Funder stewardship and grant renewal management with Right
                      Cause:{" "}
                    </strong>
                  </p>

                  <ul>
                    <li>
                      Stewardship plans schedule post-grant touchpoints
                      automatically{" "}
                    </li>
                    <li>
                      Impact reports pull from the same data used for compliance
                      reporting{" "}
                    </li>
                    <li>
                      Renewal pipeline carries forward the full relationship
                      history{" "}
                    </li>
                    <li>
                      Lessons learned route back into prospect research so the
                      team gets smarter every cycle{" "}
                    </li>
                  </ul>

                  <p>
                    <strong>Why Right Cause Nonprofit CRM? </strong>
                  </p>
                  <p>
                    For most of the nonprofit leaders we work with, the
                    conversation eventually comes down to two questions:{" "}
                    <i>
                      Will this fit the way our team already works? And can our
                      IT director support it?
                    </i>
                  </p>

                  <p>
                    Right Cause is built on the Microsoft Cloud for a reason.
                    Your team likely already uses Microsoft 365 every day. Your
                    finance team may already be on Business Central or another
                    Dynamics 365 module. Your IT environment is likely secured
                    through Entra ID with conditional access policies your board
                    has come to expect.
                  </p>

                  <p>What this means in practice for your organization: </p>

                  <ul>
                    <li>
                      <strong>Single sign-on through Entra ID.</strong> No new
                      credentials for staff to manage.{" "}
                    </li>
                    <li>
                      <strong>Native integration with Microsoft 365. </strong>
                      Outlook for funder communications, Teams for
                      collaboration, SharePoint for document management, Power
                      BI for executive dashboards.{" "}
                    </li>
                    <li>
                      <strong>Power Automate workflows.</strong> Approval
                      routing, deadline alerts, and compliance checklists
                      configured to your processes, not ours.{" "}
                    </li>
                    <li>
                      <strong>
                        Microsoft data security and compliance posture.
                      </strong>{" "}
                      The same enterprise-grade infrastructure your auditors and
                      major funders already trust.{" "}
                    </li>
                    <li>
                      <strong>Eligible nonprofit licensing.</strong> We help
                      eligible 501(c)(3) organizations make the most of
                      Microsoft's nonprofit pricing and grant programs.{" "}
                    </li>
                  </ul>

                  <p>
                    You are not buying a standalone tool that your team must
                    learn from scratch. You are extending the platform you
                    already trust into the one workflow that has been hardest to
                    get right.
                  </p>

                  <h2 className="mar-top10" id="core-capabilities">
                    Right Cause Grants Accelerator: Core Capabilities Capability
                    What It Does
                  </h2>

                  {/* table start  */}

                  <div class="capability-table-wrapper">
                    <table class="capability-table">
                      <thead>
                        <tr>
                          <th>Capability</th>
                          <th>What It Does</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <span>Funder Intelligence</span>
                          </td>
                          <td>
                            Profiles, giving history, fit scoring, prospect
                            pipeline
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <span>Proposal Workflow</span>
                          </td>
                          <td>
                            Version control, budget builder, approval routing
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <span>Award Management</span>
                          </td>
                          <td>
                            Agreements, special conditions, period of
                            performance tracking
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <span>Financial Operations</span>
                          </td>
                          <td>
                            Disbursements, drawdowns, allocations, BvA
                            dashboards
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <span>Subrecipient Monitoring</span>
                          </td>
                          <td>
                            Risk assessments, subaward tracking, site visits
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <span>Compliance Engine</span>
                          </td>
                          <td>
                            Funder-specific checklists, audit trail, retention
                            rules
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <span>Reporting Suite</span>
                          </td>
                          <td>
                            Auto-generated narrative, financial, and M&amp;E
                            reports
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <span>Microsoft 365 Integration</span>
                          </td>
                          <td>
                            Outlook, Teams, SharePoint, Power BI native
                            integration
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <span>Role-Based Access</span>
                          </td>
                          <td>
                            PI, Grant Manager, Finance, Compliance, Read-Only
                            views
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <span>Audit-Ready by Default</span>
                          </td>
                          <td>Every action logged, every document versioned</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* table end  */}

                  <h2 className="mar-top10" id="conversations">
                    What a Conversation with Us Looks Like
                  </h2>

                  <p>
                    <strong>
                      We do not lead with a demo. We lead with discovery.{" "}
                    </strong>
                  </p>

                  <p>
                    When we sit down with a nonprofit team that can offer grant
                    management best practices, the first hour is spent
                    understanding your portfolio. How many active grants? What
                    mix of federal, foundation, and corporate? Where is your
                    team currently losing time? What did your last audit flag?
                    What does your IT environment look like today?{" "}
                    <a href="https://www.rightcause.net/" target="_blank">
                      Connect with us
                    </a>{" "}
                    &nbsp; for questions related to grant lifecycle and best
                    practices.
                  </p>

                  <div class="blogCta-wrapper">
                    <div class="row align-items-center">
                      <div class="col-md-4 col-lg-2">
                        <div class="cta-image blog-cta-banner-img">
                          <img
                            alt="Non-Profit CRM - No bank details required"
                            src={rcCtaLogo}
                            width="77"
                            height="112"
                          />
                        </div>
                      </div>
                      <div class="col-md-8 col-lg-10">
                        <div class="cta-text blog-cta-banner">
                          <h5>
                            Explore the Right Cause Grant Management
                            Accelerator.
                          </h5>
                          <p>
                            Avail your Free Trial OR Test-drive using your
                            Outlook email ID.
                          </p>
                          <a
                            href="https://appsource.microsoft.com/en-us/product/dynamics-365/beyond_key_systems.rightcauseoffer?tab=Overview"
                            className="rc-primary-btn rc-large-btn rc-btn mt-xl-3"
                            tabindex="0"
                            aria-label="Request Demo Button"
                            target="_blank"
                          >
                            Available on AppSource
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4 col-lg-4">
                <div class="b-sideblock tableof-content">
                  <h3 class="headingThree">Table of contents</h3>
                  <div class="ba-sidebar-content">
                    <ul>
                      <li>
                        Introduction
                        <ul>
                          <li>
                            <a href="#grants-management-process">
                              Why a General-Purpose CRM Is Not Enough
                            </a>
                          </li>
                          <li>
                            <a href="#phase-1">
                              Phase 1: Prospecting and Opportunity Management
                            </a>
                          </li>
                          <li>
                            <a href="#phase-2">
                              Phase 2: Cultivation and Pre-Application{" "}
                            </a>
                          </li>
                          <li>
                            <a href="#phase-3">
                              Phase 3: Proposal Development and Submission
                            </a>
                          </li>
                          <li>
                            <a href="#phase-4">
                              Phase 4: Award Decision and Agreement
                            </a>
                          </li>
                          <li>
                            <a href="#phase-5">
                              Phase 5: Award Setup and Activation
                            </a>
                          </li>
                          <li>
                            <a href="#phase-6">
                              Phase 6: Fund Allocation and Disbursement{" "}
                            </a>
                          </li>
                          <li>
                            <a href="#phase-7">
                              Phase 7: Implementation and Budget Monitoring
                            </a>
                          </li>
                          <li>
                            <a href="#phase-8">
                              Phase 8: Monitoring, Evaluation, and Reporting
                            </a>
                          </li>
                          <li>
                            <a href="#phase-9">Phase 9: Close-Out</a>
                          </li>
                          <li>
                            <a href="#phase-10">
                              Phase 10: Stewardship and Renewal
                            </a>
                          </li>
                          <li>
                            <a href="#core-capabilities">
                              Right Cause Grants Accelerator: Core Capabilities
                            </a>
                          </li>
                          <li>
                            <a href="#conversations">
                              What a Conversation with Us Looks Like
                            </a>
                          </li>
                        </ul>
                      </li>
                    </ul>
                  </div>
                </div>

                <div class="b-sideblock newsletter blog-news-subs">
                  <h2 class="h1 text-center blueTxt">
                    Subscribe To Our Newsletter
                  </h2>
                  <NewsLetter />
                  <div class="text-center mt-5">
                    <img
                      src={newsletterbanner}
                      alt="Subscribe To Our Newsletter"
                      width="286"
                      height="223"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default TheGrantLifecycle;
